import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import TestimonialsCarousel from '../components/TestimonialsCarousel';
import { 
  Sparkles, FileText, ClipboardList, BookOpen, FileCheck, 
  Grid3x3, GraduationCap, BookMarked, Calculator, Microscope,
  Globe2, CheckCircle2, Clock, Shield, Heart, TrendingUp,
  ArrowRight, Download, Lightbulb
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function LandingPage() {
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterName, setNewsletterName] = useState('');
  const [newsletterConsent, setNewsletterConsent] = useState(false);
  const [subscribing, setSubscribing] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    
    if (!newsletterEmail.trim()) {
      toast.error('Please enter your email');
      return;
    }

    if (!newsletterConsent) {
      toast.error('Please confirm you want to receive teaching tips');
      return;
    }

    setSubscribing(true);
    try {
      const response = await axios.post(`${API}/newsletter/subscribe`, {
        email: newsletterEmail,
        first_name: newsletterName || null,
        consent: newsletterConsent
      });
      
      toast.success(response.data.message);
      setNewsletterEmail('');
      setNewsletterName('');
      setNewsletterConsent(false);
    } catch (error) {
      console.error('Newsletter error:', error);
      toast.error(error.response?.data?.detail || 'Failed to subscribe. Please try again.');
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <section className="py-16 sm:py-24 px-6 sm:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-7">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight font-bold text-slate-900 mb-6" data-testid="hero-title">
                Helping Teachers Save Time. Helping Every Child Learn.
              </h1>
              
              <p className="text-xl text-blue-600 font-semibold mb-6">
                Because Every Student Deserves an Opportunity to Learn.
              </p>
              
              <p className="text-base text-slate-600 leading-relaxed mb-6 max-w-2xl">
                Discover classroom-tested worksheets, lesson plans, assessments, notes, teaching ideas and practical resources created by a real teacher for real classrooms.
              </p>
              
              <div className="bg-slate-50 rounded-2xl border border-slate-200 p-6 mb-8">
                <p className="text-base font-semibold text-slate-900 mb-2">
                  Need something customized?
                </p>
                <p className="text-sm text-slate-600">
                  Use our AI teaching assistant to generate additional classroom resources in minutes.
                </p>
              </div>
              
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/browse"
                  data-testid="hero-browse-cta"
                  className="bg-blue-600 text-white rounded-xl px-8 py-4 font-semibold shadow-sm hover:bg-blue-700 hover:shadow-md transition-all active:scale-95 inline-flex items-center gap-2"
                >
                  <BookOpen className="w-5 h-5" strokeWidth={1.5} />
                  Browse Resources
                </Link>
                
                <Link
                  to="/ai-tools"
                  data-testid="hero-ai-cta"
                  className="bg-white text-slate-700 border-2 border-slate-200 rounded-xl px-8 py-4 font-semibold hover:border-slate-300 hover:bg-slate-50 transition-all active:scale-95"
                >
                  Explore AI Tools
                </Link>
              </div>
              
              <p className="text-sm text-slate-500 mt-6">
                ✨ Created by a teacher, for teachers and parents
              </p>
            </div>
            
            <div className="lg:col-span-5">
              <div className="rounded-3xl overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.08)]">
                <img 
                  src="https://images.unsplash.com/photo-1503676260728-1c00da094a0b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3MDgyNTV8MHwxfHNlYXJjaHw1fHxjaGlsZHJlbiUyMGxlYXJuaW5nJTIwY2xhc3Nyb29tfGVufDB8fHx8MTczMjU0NzY4N3ww&ixlib=rb-4.1.0&q=85"
                  alt="Children learning in classroom"
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Featured Resources Section */}
      <section className="py-16 sm:py-24 px-6 sm:px-8 lg:px-12 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight text-slate-900 font-bold mb-4">
              Classroom-Ready Teaching Resources
            </h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto">
              Browse practical resources created and tested in real primary classrooms
            </p>
          </div>
          
          <p className="text-center text-sm text-slate-500 mb-6">
            📚 New resources added regularly
          </p>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {[
              { name: 'Worksheets', icon: FileText, count: '3', desc: 'Practice & revision', category: 'worksheet' },
              { name: 'Test Papers', icon: ClipboardList, count: '2', desc: 'Ready-to-use assessments', category: 'test_paper' },
              { name: 'Lesson Plans', icon: BookOpen, count: '1', desc: 'Structured lessons', category: 'lesson_plan' },
              { name: 'Teaching Notes', icon: BookMarked, count: '0', desc: 'Concept explanations', category: 'teaching_notes' },
              { name: 'Reading Resources', icon: FileCheck, count: '0', desc: 'Comprehension passages', category: 'reading_resource' },
              { name: 'Teacher Tips', icon: Lightbulb, count: '6', desc: 'Practical classroom ideas', route: '/teacher-tips' }
            ].map((item, index) => {
              const Icon = item.icon;
              const isTeachingTips = item.name === 'Teacher Tips';
              const linkTo = isTeachingTips ? item.route : `/resources?category=${item.category}`;
              
              return (
                <Link
                  key={index}
                  to={linkTo}
                  className="bg-white rounded-2xl border border-slate-100 p-6 hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-300 cursor-pointer block"
                  data-testid={`featured-category-${index}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="bg-blue-50 rounded-xl p-3 inline-block">
                      <Icon className="w-6 h-6 text-blue-600" strokeWidth={1.5} />
                    </div>
                    <span className="text-xs font-semibold text-slate-500 bg-slate-100 px-3 py-1 rounded-full">
                      {item.count}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-1">{item.name}</h3>
                  <p className="text-sm text-slate-600">{item.desc}</p>
                </Link>
              );
            })}
          </div>
          
          <div className="text-center">
            <Link
              to="/browse"
              data-testid="featured-browse-cta"
              className="bg-blue-600 text-white rounded-xl px-8 py-4 font-semibold shadow-sm hover:bg-blue-700 hover:shadow-md transition-all active:scale-95 inline-flex items-center gap-2"
            >
              View All Resources
              <ArrowRight className="w-5 h-5" strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>
      
      {/* Meet Zeenat Section */}
      <section className="py-16 sm:py-20 px-6 sm:px-8 lg:px-12">
        <div className="max-w-5xl mx-auto">
          <div className="bg-white rounded-3xl border border-slate-100 p-8 sm:p-12 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            <div className="grid md:grid-cols-12 gap-8 items-center">
              <div className="md:col-span-4 text-center">
                <div className="w-40 h-40 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 mx-auto mb-4 flex items-center justify-center border-4 border-white shadow-lg">
                  <span className="text-5xl font-bold text-blue-600">Z</span>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-center gap-2 text-sm text-slate-600">
                    <CheckCircle2 className="w-4 h-4 text-blue-600" strokeWidth={2} />
                    <span>5+ Years Teaching</span>
                  </div>
                  <div className="flex items-center justify-center gap-2 text-sm text-slate-600">
                    <CheckCircle2 className="w-4 h-4 text-blue-600" strokeWidth={2} />
                    <span>Classroom-Tested</span>
                  </div>
                </div>
              </div>
              
              <div className="md:col-span-8">
                <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">
                  Meet Zeenat
                </h2>
                <p className="text-base text-slate-700 leading-relaxed mb-4">
                  Hi, I'm Zeenat. For over five years I've been teaching primary students and creating my own worksheets, lesson plans, assessments and classroom resources.
                </p>
                <p className="text-base text-slate-700 leading-relaxed mb-4">
                  Over the years, I've been fortunate to receive immense love from both parents and students because of my creative approaches to teaching even the most complex concepts in simple and engaging ways.
                </p>
                <p className="text-base text-slate-700 leading-relaxed mb-4">
                  Many teachers spend countless hours creating resources after school. I wanted to change that. I created Learn with Zeenat so teachers can save those valuable hours and spend more time with what truly matters—their students and their families.
                </p>
                <p className="text-base text-slate-700 leading-relaxed mb-4">
                  I also strongly believe that every child can learn. Sometimes the child doesn't need to change. The approach does.
                </p>
                <p className="text-base text-slate-700 leading-relaxed mb-6">
                  That's why every resource I create is designed to make learning meaningful, engaging and accessible for all learners.
                </p>
                <p className="text-lg font-semibold text-blue-600">
                  Because every student deserves an opportunity to learn.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Helping Every Child Learn - Differentiation Section */}
      <section className="py-16 sm:py-24 px-6 sm:px-8 lg:px-12 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight text-slate-900 font-bold mb-4">
              Helping Every Child Learn
            </h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto leading-relaxed">
              Every classroom is different. Every learner is different.<br />
              Teaching should be too.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <div className="bg-blue-50 rounded-xl p-3 inline-block mb-4">
                <CheckCircle2 className="w-6 h-6 text-blue-600" strokeWidth={1.5} />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">
                Resources for Struggling Learners
              </h3>
              <p className="text-sm text-slate-600">
                Scaffolded activities and visual supports to build confidence
              </p>
            </div>
            
            <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <div className="bg-blue-50 rounded-xl p-3 inline-block mb-4">
                <CheckCircle2 className="w-6 h-6 text-blue-600" strokeWidth={1.5} />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">
                Extension Activities
              </h3>
              <p className="text-sm text-slate-600">
                Challenge advanced learners with deeper tasks
              </p>
            </div>
            
            <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <div className="bg-blue-50 rounded-xl p-3 inline-block mb-4">
                <CheckCircle2 className="w-6 h-6 text-blue-600" strokeWidth={1.5} />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">
                Visual Learning Strategies
              </h3>
              <p className="text-sm text-slate-600">
                Diagrams, charts and visual aids for every topic
              </p>
            </div>
            
            <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <div className="bg-blue-50 rounded-xl p-3 inline-block mb-4">
                <CheckCircle2 className="w-6 h-6 text-blue-600" strokeWidth={1.5} />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">
                Confidence-Building Worksheets
              </h3>
              <p className="text-sm text-slate-600">
                Start easy, build up gradually, celebrate progress
              </p>
            </div>
            
            <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <div className="bg-blue-50 rounded-xl p-3 inline-block mb-4">
                <CheckCircle2 className="w-6 h-6 text-blue-600" strokeWidth={1.5} />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">
                Scaffolded Lessons
              </h3>
              <p className="text-sm text-slate-600">
                Break complex concepts into manageable steps
              </p>
            </div>
            
            <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <div className="bg-blue-50 rounded-xl p-3 inline-block mb-4">
                <CheckCircle2 className="w-6 h-6 text-blue-600" strokeWidth={1.5} />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">
                Practical Differentiation Ideas
              </h3>
              <p className="text-sm text-slate-600">
                Easy-to-implement strategies for every lesson
              </p>
            </div>
          </div>
          
          <div className="text-center">
            <p className="text-lg text-slate-700 italic max-w-2xl mx-auto">
              "I believe every child can learn. Sometimes we simply need a different approach."
            </p>
            <p className="text-base text-slate-600 mt-2">— Zeenat</p>
          </div>
        </div>
      </section>
      
      {/* Why Teachers Trust Section */}
      <section className="py-16 sm:py-24 px-6 sm:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight text-slate-900 font-bold mb-4">
              Why Teachers Trust Learn with Zeenat
            </h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto">
              Every resource is designed with real classroom needs in mind
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="trust-feature-0">
              <div className="bg-blue-50 rounded-xl p-3 inline-block mb-4">
                <CheckCircle2 className="w-6 h-6 text-blue-600" strokeWidth={1.5} />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">
                Created by an Experienced Teacher
              </h3>
              <p className="text-sm text-slate-600">
                Built by someone who knows real classroom challenges
              </p>
            </div>
            
            <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="trust-feature-1">
              <div className="bg-blue-50 rounded-xl p-3 inline-block mb-4">
                <Shield className="w-6 h-6 text-blue-600" strokeWidth={1.5} />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">
                Classroom-Tested Resources
              </h3>
              <p className="text-sm text-slate-600">
                Every resource is tested in real primary classrooms
              </p>
            </div>
            
            <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="trust-feature-2">
              <div className="bg-blue-50 rounded-xl p-3 inline-block mb-4">
                <BookOpen className="w-6 h-6 text-blue-600" strokeWidth={1.5} />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">
                Curriculum-Aligned
              </h3>
              <p className="text-sm text-slate-600">
                Aligned with primary curriculum standards and objectives
              </p>
            </div>
            
            <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="trust-feature-3">
              <div className="bg-blue-50 rounded-xl p-3 inline-block mb-4">
                <FileText className="w-6 h-6 text-blue-600" strokeWidth={1.5} />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">
                Ready to Print
              </h3>
              <p className="text-sm text-slate-600">
                Download and print instantly - no editing needed
              </p>
            </div>
            
            <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="trust-feature-4">
              <div className="bg-blue-50 rounded-xl p-3 inline-block mb-4">
                <Clock className="w-6 h-6 text-blue-600" strokeWidth={1.5} />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">
                Saves Hours Every Week
              </h3>
              <p className="text-sm text-slate-600">
                Generate in minutes what takes hours to create manually
              </p>
            </div>
            
            <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="trust-feature-5">
              <div className="bg-blue-50 rounded-xl p-3 inline-block mb-4">
                <Sparkles className="w-6 h-6 text-blue-600" strokeWidth={1.5} />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">
                AI Powered but Teacher Approved
              </h3>
              <p className="text-sm text-slate-600">
                Smart technology guided by real teaching experience
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* AI Teaching Assistant Section */}
      <section className="py-16 sm:py-24 px-6 sm:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight text-slate-900 font-bold mb-4">
              AI Teaching Assistant
            </h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto">
              Need something customized? Our AI tools can help you generate additional classroom resources based on your specific needs.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-2xl border-2 border-blue-100 p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-300" data-testid="ai-tool-card-0">
              <div className="bg-blue-600 rounded-xl p-3 inline-block mb-4">
                <FileText className="w-6 h-6 text-white" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-2">Worksheet Generator</h3>
              <p className="text-base text-slate-600 mb-4">Create custom worksheets with practice problems, activities, and visual elements.</p>
              <Link 
                to="/ai-tools"
                className="text-blue-600 font-medium text-sm inline-flex items-center gap-1 hover:gap-2 transition-all"
              >
                Try Now <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
            </div>
            
            <div className="bg-white rounded-2xl border-2 border-blue-100 p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-300" data-testid="ai-tool-card-1">
              <div className="bg-blue-600 rounded-xl p-3 inline-block mb-4">
                <ClipboardList className="w-6 h-6 text-white" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-2">Test Paper Generator</h3>
              <p className="text-base text-slate-600 mb-4">Generate comprehensive tests with multiple question types and marking schemes.</p>
              <Link 
                to="/ai-tools"
                className="text-blue-600 font-medium text-sm inline-flex items-center gap-1 hover:gap-2 transition-all"
              >
                Try Now <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
            </div>
            
            <Link to="/ai-tools/lesson-planner" className="bg-white rounded-2xl border border-slate-200 p-8 hover:shadow-lg transition-all" data-testid="ai-tool-card-2">
              <div className="bg-blue-100 rounded-xl p-3 inline-block mb-4">
                <BookOpen className="w-6 h-6 text-blue-600" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Lesson Planner</h3>
              <p className="text-base text-slate-600">Structured lesson plans with objectives and activities.</p>
            </Link>
            
            <Link to="/assessment" className="bg-white rounded-2xl border border-slate-200 p-8 hover:shadow-lg transition-all" data-testid="ai-tool-card-3">
              <div className="bg-purple-100 rounded-xl p-3 inline-block mb-4">
                <FileCheck className="w-6 h-6 text-purple-600" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Assessment Hub</h3>
              <p className="text-base text-slate-600">AI-powered test marking with rubrics and feedback.</p>
            </Link>
            
            <Link to="/parent/activity-suggester" className="bg-white rounded-2xl border border-slate-200 p-8 hover:shadow-lg transition-all" data-testid="ai-tool-card-4">
              <div className="bg-green-100 rounded-xl p-3 inline-block mb-4">
                <Grid3x3 className="w-6 h-6 text-green-600" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Activity Suggester</h3>
              <p className="text-base text-slate-600">Fun educational activities for home learning.</p>
            </Link>
          </div>
          
          <div className="text-center mt-12">
            <Link
              to="/ai-tools"
              data-testid="ai-section-cta"
              className="bg-blue-600 text-white rounded-xl px-6 py-3 font-medium shadow-sm hover:bg-blue-700 hover:shadow-md transition-all active:scale-95 inline-flex items-center gap-2"
            >
              Try AI Tools Free
              <ArrowRight className="w-5 h-5" strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>
      
      {/* Teaching Resources Section */}
      <section className="py-16 sm:py-24 px-6 sm:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight text-slate-900 font-bold mb-4">
              Teacher Tips & Classroom Ideas
            </h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto">
              Practical strategies and resources ready to use in your classroom
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
            {[
              { name: 'Worksheets', icon: FileText },
              { name: 'Test Papers', icon: ClipboardList },
              { name: 'Lesson Plans', icon: BookOpen },
              { name: 'Notes', icon: BookMarked },
              { name: 'Mathematics', icon: Calculator },
              { name: 'Science', icon: Microscope },
              { name: 'Social Studies', icon: Globe2 },
              { name: 'Quizzes', icon: Grid3x3 }
            ].map((item, index) => {
              const Icon = item.icon;
              return (
                <div
                  key={index}
                  className="bg-white rounded-xl border border-slate-100 p-6 hover:border-blue-200 hover:shadow-sm transition-all cursor-pointer group"
                  data-testid={`resource-category-${index}`}
                >
                  <div className="bg-blue-50 rounded-lg p-2 inline-block mb-3 group-hover:bg-blue-100 transition-colors">
                    <Icon className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
                  </div>
                  <h4 className="text-lg font-medium text-slate-800">{item.name}</h4>
                </div>
              );
            })}
          </div>
          
          <div className="text-center">
            <Link
              to="/browse"
              data-testid="resources-browse-cta"
              className="bg-white text-blue-600 border-2 border-blue-100 rounded-xl px-6 py-3 font-medium hover:border-blue-600 hover:bg-blue-50 transition-all active:scale-95 inline-flex items-center gap-2"
            >
              Browse All Resources
              <ArrowRight className="w-5 h-5" strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>
      
      {/* Created for Teachers and Parents Section */}
      <section className="py-16 sm:py-24 px-6 sm:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight text-slate-900 font-bold mb-4">
              Created for Teachers and Parents
            </h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto">
              Supporting both educators and families in helping every child succeed
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl border border-slate-100 p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <div className="bg-blue-50 rounded-xl p-3 inline-block mb-6">
                <GraduationCap className="w-8 h-8 text-blue-600" strokeWidth={1.5} />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-6">For Teachers</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" strokeWidth={2} />
                  <span className="text-base text-slate-700">Ready-to-use worksheets</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" strokeWidth={2} />
                  <span className="text-base text-slate-700">Lesson plans</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" strokeWidth={2} />
                  <span className="text-base text-slate-700">Assessments</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" strokeWidth={2} />
                  <span className="text-base text-slate-700">Teaching ideas</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" strokeWidth={2} />
                  <span className="text-base text-slate-700">Classroom differentiation</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" strokeWidth={2} />
                  <span className="text-base text-slate-700 font-semibold">Save hours every week</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-white rounded-2xl border border-slate-100 p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <div className="bg-blue-50 rounded-xl p-3 inline-block mb-6">
                <Heart className="w-8 h-8 text-blue-600" strokeWidth={1.5} />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-6">For Parents</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" strokeWidth={2} />
                  <span className="text-base text-slate-700">Extra home practice</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" strokeWidth={2} />
                  <span className="text-base text-slate-700">Revision worksheets</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" strokeWidth={2} />
                  <span className="text-base text-slate-700">Reading support</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" strokeWidth={2} />
                  <span className="text-base text-slate-700">Confidence-building activities</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" strokeWidth={2} />
                  <span className="text-base text-slate-700 font-semibold">Help struggling learners</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
      
      {/* Join Community Section */}
      <section className="py-16 sm:py-24 px-6 sm:px-8 lg:px-12 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-3xl p-12 text-center shadow-xl">
            <div className="w-16 h-16 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-6">
              <GraduationCap className="w-8 h-8 text-white" strokeWidth={1.5} />
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight text-white font-bold mb-4">
              Join the Learn with Zeenat Community
            </h2>
            <p className="text-base text-blue-50 leading-relaxed mb-8 max-w-2xl mx-auto">
              Get practical teaching ideas, classroom resources and exclusive updates delivered weekly.
            </p>
            <Link
              to="/browse"
              data-testid="community-cta"
              className="bg-white text-blue-600 rounded-xl px-8 py-4 font-semibold shadow-sm hover:bg-blue-50 transition-all active:scale-95 inline-flex items-center gap-2"
            >
              Join Free
              <ArrowRight className="w-5 h-5" strokeWidth={1.5} />
            </Link>
            <p className="text-sm text-blue-100 mt-6">
              ✨ Free resources. Practical tips. Real classroom strategies.
            </p>
          </div>
        </div>
      </section>
      
      {/* Newsletter Section */}
      <section className="py-16 sm:py-24 px-6 sm:px-8 lg:px-12 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-3xl border border-slate-100 p-12 text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight text-slate-900 font-bold mb-4">
              Get One Practical Teaching Tip Every Week
            </h2>
            <p className="text-base text-slate-600 leading-relaxed mb-8 max-w-2xl mx-auto">
              Join teachers receiving practical classroom strategies, worksheets and AI teaching ideas delivered weekly.
            </p>
            
            <form onSubmit={handleNewsletterSubmit} className="max-w-md mx-auto">
              <div className="flex flex-col gap-3">
                <input
                  type="text"
                  value={newsletterName}
                  onChange={(e) => setNewsletterName(e.target.value)}
                  placeholder="First name (optional)"
                  className="rounded-xl border-2 border-slate-200 bg-white px-4 py-3 text-base focus:border-blue-600 focus:ring-2 focus:ring-blue-100 outline-none transition-all"
                />
                <input
                  type="email"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  placeholder="Email address *"
                  data-testid="newsletter-email-input"
                  className="rounded-xl border-2 border-slate-200 bg-white px-4 py-3 text-base focus:border-blue-600 focus:ring-2 focus:ring-blue-100 outline-none transition-all"
                  required
                />
                
                <label className="flex items-start gap-2 text-left text-sm text-slate-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newsletterConsent}
                    onChange={(e) => setNewsletterConsent(e.target.checked)}
                    className="mt-0.5 w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                    required
                  />
                  <span>
                    I want to receive practical teaching tips, classroom strategies, and resource updates from Learn with Zeenat. (You can unsubscribe anytime.)
                  </span>
                </label>
                
                <button
                  type="submit"
                  disabled={subscribing || !newsletterConsent}
                  data-testid="newsletter-submit-btn"
                  className="bg-blue-600 text-white rounded-xl px-6 py-3 font-medium shadow-sm hover:bg-blue-700 transition-all active:scale-95 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {subscribing ? 'Subscribing...' : 'Subscribe Free'}
                </button>
              </div>
            </form>
            
            <p className="text-sm text-slate-500 mt-4">
              ✨ No spam. Unsubscribe anytime.
            </p>
          </div>
        </div>
      </section>
      
      {/* Testimonials Section */}
      <section className="py-16 sm:py-24 px-6 sm:px-8 lg:px-12 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight text-slate-900 font-bold mb-4">
              Loved by Teachers and Parents
            </h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto">
              See what educators and parents are saying about Learn with Zeenat
            </p>
          </div>
          
          <TestimonialsCarousel />
        </div>
      </section>
      
      {/* Created in Real Classrooms Section */}
      <section className="py-16 sm:py-24 px-6 sm:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight text-slate-900 font-bold mb-4">
              Created in Real Classrooms
            </h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto">
              Every resource is designed with practical classroom needs in mind
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-2xl border border-slate-100 p-6 text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <div className="bg-blue-50 rounded-xl p-3 inline-block mb-3">
                <CheckCircle2 className="w-6 h-6 text-blue-600" strokeWidth={2} />
              </div>
              <h4 className="font-semibold text-slate-800">Designed by a Teacher</h4>
            </div>
            
            <div className="bg-white rounded-2xl border border-slate-100 p-6 text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <div className="bg-blue-50 rounded-xl p-3 inline-block mb-3">
                <CheckCircle2 className="w-6 h-6 text-blue-600" strokeWidth={2} />
              </div>
              <h4 className="font-semibold text-slate-800">Classroom Tested</h4>
            </div>
            
            <div className="bg-white rounded-2xl border border-slate-100 p-6 text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <div className="bg-blue-50 rounded-xl p-3 inline-block mb-3">
                <CheckCircle2 className="w-6 h-6 text-blue-600" strokeWidth={2} />
              </div>
              <h4 className="font-semibold text-slate-800">Editable Resources</h4>
            </div>
            
            <div className="bg-white rounded-2xl border border-slate-100 p-6 text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <div className="bg-blue-50 rounded-xl p-3 inline-block mb-3">
                <CheckCircle2 className="w-6 h-6 text-blue-600" strokeWidth={2} />
              </div>
              <h4 className="font-semibold text-slate-800">Curriculum Aligned</h4>
            </div>
            
            <div className="bg-white rounded-2xl border border-slate-100 p-6 text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <div className="bg-blue-50 rounded-xl p-3 inline-block mb-3">
                <CheckCircle2 className="w-6 h-6 text-blue-600" strokeWidth={2} />
              </div>
              <h4 className="font-semibold text-slate-800">Print Ready</h4>
            </div>
            
            <div className="bg-white rounded-2xl border border-slate-100 p-6 text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <div className="bg-blue-50 rounded-xl p-3 inline-block mb-3">
                <CheckCircle2 className="w-6 h-6 text-blue-600" strokeWidth={2} />
              </div>
              <h4 className="font-semibold text-slate-800">Constantly Updated</h4>
            </div>
          </div>
        </div>
      </section>
      
      {/* Future Vision Section */}
      <section className="py-16 sm:py-24 px-6 sm:px-8 lg:px-12 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-3xl border border-slate-100 p-12">
            <div className="text-center max-w-3xl mx-auto">
              <div className="bg-blue-50 rounded-xl p-3 inline-block mb-6">
                <TrendingUp className="w-8 h-8 text-blue-600" strokeWidth={1.5} />
              </div>
              
              <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight text-slate-900 font-bold mb-4">
                Building the Future of Teaching
              </h2>
              
              <p className="text-base text-slate-600 leading-relaxed mb-8">
                Learn with Zeenat is growing into a complete platform for primary teachers. Here's what's coming:
              </p>
              
              <div className="grid sm:grid-cols-2 gap-4 text-left mb-8">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                  <div>
                    <h4 className="font-semibold text-slate-800">Teacher Memberships</h4>
                    <p className="text-sm text-slate-600">Unlimited access to premium resources</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                  <div>
                    <h4 className="font-semibold text-slate-800">School Solutions</h4>
                    <p className="text-sm text-slate-600">Resources for entire schools</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                  <div>
                    <h4 className="font-semibold text-slate-800">Professional Development</h4>
                    <p className="text-sm text-slate-600">Training courses for teachers</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                  <div>
                    <h4 className="font-semibold text-slate-800">Teacher Community</h4>
                    <p className="text-sm text-slate-600">Connect and collaborate with peers</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}

export default LandingPage;
